import { useState, useEffect, useCallback, useRef } from 'react'
import type { UserData, AnyEntry, ActivityType, ListKey, Intake } from '../types'
import {
  loadData,
  saveData,
  saveToLocalStorage,
  getDefaultUserData,
  isBlankData,
  type SaveFailureKind,
} from '../utils/storage'
import { listKeyForType } from '../utils/entries'
import { estimateWeightsCalories } from '../utils/calc'

/**
 * One-time backfill: fill in estimated calories for weights entries logged before
 * the effort/duration estimate existed. Only touches entries with a blank `calories`
 * where an estimate is possible (effort + reps-or-duration + body weight). Idempotent.
 * Returns the (possibly new) list and whether anything changed.
 */
function backfillWeightsCalories(
  list: AnyEntry[],
  weight: number,
): { list: AnyEntry[]; changed: boolean } {
  let changed = false
  const next = list.map((raw) => {
    const e = raw as unknown as Record<string, string>
    if (e.calories) return raw
    const kcal = estimateWeightsCalories(e.intensity, e.duration, e.repetitions, weight, e.kg)
    if (kcal === null) return raw
    changed = true
    return { ...e, calories: String(kcal) } as unknown as AnyEntry
  })
  return { list: changed ? next : list, changed }
}

/** Return a copy of `prev` with one list replaced (avoids LHS-cast issues). */
function withList(prev: UserData, lk: ListKey, list: AnyEntry[]): UserData {
  const lists = { ...prev.lists } as Record<ListKey, AnyEntry[]>
  lists[lk] = list
  return { ...prev, lists: lists as unknown as UserData['lists'] }
}

export type LoadMode = 'remote' | 'offline-cache' | 'default' | 'error'

export interface SaveError {
  kind: SaveFailureKind
  message: string
  at: number
  status?: number
}

export interface UseUserResult {
  data: UserData
  isLoading: boolean
  isInitialized: boolean
  error: string | null
  loadMode: LoadMode
  offlineSince: number | null
  saveError: SaveError | null
  updateData: (updates: Partial<UserData>) => void
  addEntry: (type: ActivityType, entry: AnyEntry) => void
  updateEntry: (type: ActivityType, index: number, entry: AnyEntry) => void
  deleteEntry: (type: ActivityType, index: number) => void
  addIntake: (intake: Intake) => void
  updateIntake: (index: number, intake: Intake) => void
  deleteIntake: (index: number) => void
  reload: () => void
  getCachedPassword: () => string | null
  cachePassword: (password: string) => void
  clearPassword: () => void
}

export function useUser(userId: string | undefined): UseUserResult {
  const [data, setData] = useState<UserData>(getDefaultUserData())
  const [isLoading, setIsLoading] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadMode, setLoadMode] = useState<LoadMode>('remote')
  const [offlineSince, setOfflineSince] = useState<number | null>(null)
  const [saveError, setSaveError] = useState<SaveError | null>(null)
  const [reloadTick, setReloadTick] = useState(0)

  const allowSavesRef = useRef(false)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previousDataRef = useRef<UserData>(getDefaultUserData())
  const passwordRef = useRef<string | null>(null)

  const processAndSetData = useCallback((raw: UserData) => {
    let loaded: UserData = { ...raw, weight: Number(raw.weight) || 0 }
    const operation = (loaded.lists?.operation as AnyEntry[] | undefined) ?? []
    const { list, changed } = backfillWeightsCalories(operation, loaded.weight)
    if (changed) loaded = withList(loaded, 'operation', list)
    setData(loaded)
    previousDataRef.current = loaded
    allowSavesRef.current = true
  }, [])

  // Load on mount / userId change / reload()
  useEffect(() => {
    if (!userId) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    async function load() {
      setIsLoading(true)
      setError(null)
      setLoadMode('remote')
      setOfflineSince(null)
      allowSavesRef.current = false

      try {
        const result = await loadData(userId!)
        if (cancelled) return
        if (result.source === 'local-offline') {
          setLoadMode('offline-cache')
          setOfflineSince(result.offlineSince)
        } else if (result.source === 'default') {
          setLoadMode('default')
        } else {
          setLoadMode('remote')
        }
        processAndSetData(result.data)
      } catch (e) {
        if (cancelled) return
        console.error('Failed to load data:', e)
        setError('Failed to load your data.')
        setLoadMode('error')
      } finally {
        if (!cancelled) {
          setIsLoading(false)
          setIsInitialized(true)
        }
      }
    }

    load()
    return () => {
      cancelled = true
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    }
  }, [userId, reloadTick, processAndSetData])

  // Debounced save when data changes
  useEffect(() => {
    if (!userId || !allowSavesRef.current || !isInitialized) return

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)

    if (isBlankData(data) && !isBlankData(previousDataRef.current)) {
      console.error('BLOCKED: attempted to save blank data over existing data')
      return
    }

    saveTimeoutRef.current = setTimeout(async () => {
      saveToLocalStorage(data)
      const result = await saveData(userId, data, previousDataRef.current)
      if (!result.success) {
        setSaveError({
          kind: result.kind || 'unknown',
          message: result.error || 'Save failed',
          at: Date.now(),
          status: result.status,
        })
      } else {
        previousDataRef.current = data
        setSaveError(null)
      }
    }, 500)
  }, [data, userId, isInitialized])

  const updateData = useCallback((updates: Partial<UserData>) => {
    setData((prev) => ({ ...prev, ...updates }))
  }, [])

  const addEntry = useCallback((type: ActivityType, entry: AnyEntry) => {
    setData((prev) => {
      const lk = listKeyForType(type)
      return withList(prev, lk, [...(prev.lists[lk] as AnyEntry[]), entry])
    })
  }, [])

  const updateEntry = useCallback((type: ActivityType, index: number, entry: AnyEntry) => {
    setData((prev) => {
      const lk = listKeyForType(type)
      const list = [...(prev.lists[lk] as AnyEntry[])]
      if (index < 0 || index >= list.length) return prev
      list[index] = entry
      return withList(prev, lk, list)
    })
  }, [])

  const deleteEntry = useCallback((type: ActivityType, index: number) => {
    setData((prev) => {
      const lk = listKeyForType(type)
      return withList(prev, lk, (prev.lists[lk] as AnyEntry[]).filter((_, i) => i !== index))
    })
  }, [])

  const addIntake = useCallback((intake: Intake) => {
    setData((prev) => ({ ...prev, intake: [...(prev.intake ?? []), intake] }))
  }, [])

  const updateIntake = useCallback((index: number, intake: Intake) => {
    setData((prev) => {
      const list = [...(prev.intake ?? [])]
      if (index < 0 || index >= list.length) return prev
      list[index] = intake
      return { ...prev, intake: list }
    })
  }, [])

  const deleteIntake = useCallback((index: number) => {
    setData((prev) => ({ ...prev, intake: (prev.intake ?? []).filter((_, i) => i !== index) }))
  }, [])

  const reload = useCallback(() => setReloadTick((t) => t + 1), [])

  const getCachedPassword = useCallback(() => passwordRef.current, [])
  const cachePassword = useCallback((password: string) => {
    passwordRef.current = password
  }, [])
  const clearPassword = useCallback(() => {
    passwordRef.current = null
  }, [])

  return {
    data,
    isLoading,
    isInitialized,
    error,
    loadMode,
    offlineSince,
    saveError,
    updateData,
    addEntry,
    updateEntry,
    deleteEntry,
    addIntake,
    updateIntake,
    deleteIntake,
    reload,
    getCachedPassword,
    cachePassword,
    clearPassword,
  }
}
